import { Box } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import SaveShiftToggle from 'modules/shift_management/components/ShiftManagement/AddShiftSidepanel/SaveShiftToggle'
import EmployeesSection from 'modules/shift_management/components/ShiftManagement/EmployeesSection'
import PlannedBreaksSelector from 'modules/shift_management/components/ShiftManagement/PlannedBreaksSelector'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import ShiftTimePicker from 'modules/shift_management/components/ShiftManagement/ShiftTimePicker'
import TimePeriodSelector from 'modules/shift_management/components/ShiftManagement/TimePeriodSelector'
import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import { Leave } from 'modules/shift_management/lib/types'

interface Props {
  form: ShiftForm
  defaultShiftForm: DefaultShiftForm
  daysByRow?: Record<string, string[]>
  commonSelectorsForm: CommonSelectorsForm
  selectableEmployeeIds: string[]
  onAddBreak: (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => void
  plannedBreakForms: PlannedBreakForm[]
  onDeleteBreak: (breakForm: PlannedBreakForm) => void
  leave?: Leave
}

const CustomShiftSection = ({
  form,
  defaultShiftForm,
  commonSelectorsForm,
  selectableEmployeeIds,
  onAddBreak,
  plannedBreakForms,
  onDeleteBreak,
  leave,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const [{ canManageDefaultShifts }] = usePermissions()
  const selectedEmployees = commonSelectorsForm.employeeSelection?.ids ?? []

  const locationsEnabled = hasFeatureFlag('dev_location_work_areas')
  const showLocation = !hasSelection || !view.isByAreas
  const hasBreaks = hasFeatureFlag('time_settings_breaks')
  const isComplementaryHoursEnabled = hasFeatureFlag('dev_time_tracking_complementary_hours')

  const startTimeField = form.get('start_time')
  const endTimeField = form.get('end_time')

  const showTimeSelector = !hasSelection || (!!leave && view.isWeekly)

  return (
    <Box gap="s16">
      <EmployeesSection
        form={commonSelectorsForm}
        selectableEmployeeIds={selectableEmployeeIds}
        selectedEmployees={selectedEmployees}
        fromLeave={!!leave}
      />
      <Box gap="s16" flexDirection="row" justifyContent="center" alignItems="center">
        <TooltipIcon.Medium
          icon="align-text-left"
          label={i18n.t('shift_management.preview_cell.shift_name')}
          color="grey700"
        />
        <TextField
          hideLabel
          label={i18n.t('shift_management.preview_cell.shift_name')}
          placeholder={i18n.t('shift_management.preview_cell.shift_name')}
          value={form.get('name').value.toString()}
          onChange={(name) => form.get('name').set(name)}
        />
      </Box>
      <ShiftTimePicker startField={startTimeField} endField={endTimeField} newVersion />
      <ShiftOvertimeCheckbox
        form={form}
        newVersion
        isComplementaryHoursEnabled={isComplementaryHoursEnabled}
      />
      {showTimeSelector && <TimePeriodSelector form={commonSelectorsForm} leave={leave} />}
      {hasBreaks && (
        <PlannedBreaksSelector
          plannedBreakForms={plannedBreakForms}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
        />
      )}
      {locationsEnabled && showLocation && (
        <ShiftLocationPicker
          locationField={form.get('location_id')}
          workAreaField={form.get('work_area_id')}
          newVersion
        />
      )}
      {locationsEnabled && <ShiftNotesField notesField={form.get('notes')} newVersion />}
      {canManageDefaultShifts && (
        <SaveShiftToggle
          form={defaultShiftForm}
          defaultTitle={form.get('name').value}
          extraHoursValue={form.get('extra_hours').value}
        />
      )}
    </Box>
  )
}

export default observer(CustomShiftSection)
